import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { track } from '../lib/analytics';

export default function InviteAccept() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('verifying');
  const [errorDetail, setErrorDetail] = useState(null);
  const [email, setEmail] = useState(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    const token = search.get('token');
    track('beta_invite_click', { token_present: !!token });
    if (!token) {
      setState('error');
      setErrorDetail('missing_token');
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/verify?token=${encodeURIComponent(token)}`);
        setEmail(data.email);
        setState('ok');
        // Prefer ?next= from the magic-link if it's a safe in-app path; otherwise use server suggestion.
        const nextParam = search.get('next');
        const safeNext = (nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')) ? nextParam : null;
        const dest = safeNext || data.redirect_to || '/dashboard';
        track('beta_invite_verified', { redirect_to: dest });
        setTimeout(() => navigate(dest, { replace: true }), 1500);
      } catch (err) {
        const detail = err?.response?.data?.detail || 'verification_failed';
        setErrorDetail(detail);
        setState('error');
        track('beta_invite_failed', { detail });
      }
    })();
  }, [search, navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        {state === 'verifying' && (
          <>
            <div className="h-10 w-10 mx-auto border-2 border-[#d8b34a]/30 border-t-[#d8b34a] rounded-full animate-spin" />
            <p className="mt-5 text-sm text-zinc-400 font-mono tracking-wider">Verifying your invite…</p>
          </>
        )}
        {state === 'ok' && (
          <>
            <ShieldCheck className="mx-auto gold-text" size={32} />
            <h1 className="mt-5 font-display text-2xl font-bold text-white">Welcome to Talas.</h1>
            <p className="mt-3 text-sm text-zinc-400">Signed in as <span className="text-zinc-200">{email}</span>. Redirecting…</p>
          </>
        )}
        {state === 'error' && (
          <>
            <AlertTriangle className="mx-auto text-red-400" size={32} />
            <h1 className="mt-5 font-display text-2xl font-bold text-white">Invite link not valid</h1>
            <p className="mt-3 text-sm text-zinc-400">
              {errorDetail === 'invite_expired' && 'This invite expired. Ask an admin to send a new one.'}
              {errorDetail === 'invite_already_used' && 'This invite was already used. Sign in with Google or request a new invite.'}
              {errorDetail === 'invite_not_found' && 'This link wasn\'t recognized.'}
              {(errorDetail === 'missing_token' || errorDetail === 'invite_token_missing') && 'No invite token provided.'}
              {!['invite_expired','invite_already_used','invite_not_found','missing_token','invite_token_missing'].includes(errorDetail) && 'We couldn\'t verify your invite.'}
            </p>
            <Link to="/login" className="mt-6 inline-block text-sm px-5 py-3 border border-white/15 text-zinc-200 hover:border-[#d8b34a]/50 rounded-sm">
              Go to sign-in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
